import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { postTrackingLink } from '@/services/campaign';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { TextField } from '@/components/text-field';
import { Form } from '@/components/ui/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { trackingLinkSchema } from '@/schema/tracking-link-schema';
import { putTrackingLink } from '@/services/tracking-link';
import { errorHandler } from '@/lib/utils';
import { SelectField } from '@/components/select-field';
import { detectType } from '@/lib/string';
export const TrackingLinkDialog = ({ campaign, trackingLink, mode, trigger, onSucceed, onSubmit, }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const userSelectedTypeRef = React.useRef(false);
    const form = useForm({
        resolver: yupResolver(trackingLinkSchema),
        defaultValues: {
            label: trackingLink ? trackingLink.label : '',
            targetUrl: trackingLink ? trackingLink.targetUrl : '',
            type: trackingLink ? trackingLink.type : 'url',
            context: trackingLink?.context ?? 'default',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => trackingLink ? putTrackingLink(data) : postTrackingLink(data),
        onSuccess: (data) => {
            toast.success(data.message);
            setOpen(false);
            onSucceed?.();
        },
        onError: errorHandler,
    });
    const targetUrl = useWatch({ control: form.control, name: 'targetUrl' });
    const effectiveMode = mode ?? campaign?.mode ?? 'classic';
    const showPlatform = effectiveMode === 'auto_store';
    React.useEffect(() => {
        if (!targetUrl)
            return;
        if (userSelectedTypeRef.current)
            return;
        form.setValue('type', detectType(targetUrl));
    }, [form, targetUrl, userSelectedTypeRef]);
    const handleSubmit = React.useCallback((data) => {
        if (trackingLink || campaign) {
            const publicId = trackingLink ? trackingLink.publicId : campaign?.publicId;
            if (!publicId)
                return;
            mutate({ publicId, data });
            return;
        }
        onSubmit?.(data);
        setOpen(false);
    }, [campaign, mutate, onSubmit, trackingLink]);
    const handleOpen = React.useCallback((open) => {
        if (open) {
            form.reset({
                label: trackingLink ? trackingLink.label : '',
                targetUrl: trackingLink ? trackingLink.targetUrl : '',
                type: trackingLink ? trackingLink.type : 'url',
                context: trackingLink?.context ?? 'default',
            });
        }
        setOpen(open);
    }, [form, trackingLink]);
    const key = React.useMemo(() => (trackingLink ? 'edit' : 'new'), [trackingLink]);
    const buttonLabel = React.useMemo(() => {
        if (isPending) {
            if (trackingLink) {
                return t('updating');
            }
            return t('creating');
        }
        if (trackingLink) {
            return t('update');
        }
        return t('create');
    }, [isPending, t, trackingLink]);
    return (<Dialog open={open} onOpenChange={handleOpen}>
            <DialogTrigger asChild>
                {trigger ? (trigger) : (<Button type="button" size="sm">
                        {t(`tracking_links.${key}.button`, { ns: 'glossary' })}
                    </Button>)}
            </DialogTrigger>
            <DialogContent className="sm:max-w-130">
                <DialogHeader>
                    <DialogTitle>{t(`tracking_links.${key}.title`, { ns: 'glossary' })}</DialogTitle>
                    <DialogDescription>{t(`tracking_links.${key}.description`, { ns: 'glossary' })}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <TextField className="col-span-1 md:col-span-2" control={form.control} name="label" placeholder={t('campaigns.placeholders.tracking_link.label', { ns: 'glossary' })} label={t('tracking_link_label')} disabled={isPending} autoComplete="destination-label"/>

                        <div className="flex gap-2 col-span-1 md:col-span-2">
                            <SelectField label={t('tracking_link_type')} control={form.control} name="type" options={[
            { value: 'url', label: 'URL' },
            { value: 'email', label: 'E-mail' },
            { value: 'phone', label: 'Phone' },
        ]} disabled={isPending} onChange={() => {
            userSelectedTypeRef.current = true;
        }}/>
                            <TextField className="w-full" control={form.control} name="targetUrl" placeholder={t('campaigns.placeholders.tracking_link.target_url', { ns: 'glossary' })} label={t('tracking_link_target_url')} disabled={isPending} autoComplete="destination-target-url"/>
                        </div>

                        {showPlatform && (<SelectField label={t('tracking_link_context', { defaultValue: 'Platform' })} control={form.control} name="context" options={[
                { value: 'default', label: 'All devices' },
                { value: 'ios', label: 'iOS' },
                { value: 'android', label: 'Android' },
                { value: 'desktop', label: 'Desktop' },
            ]} disabled={isPending}/>)}

                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={isPending}>
                                {t('cancel', { defaultValue: 'Cancel' })}
                            </Button>
                            <Button type="submit" disabled={isPending} onClick={form.handleSubmit(handleSubmit)}>
                                {buttonLabel}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>);
};
TrackingLinkDialog.displayName = 'TrackingLinkDialog';
